import { extent } from "d3-array"
import memoize from "memoize-one"

import { getRangeWithoutOutliers, filterOnlineOfflineData } from "../../resources/utils"
import { isNumber } from "../../../utils"

export class ObsVsObsPlotBaton {
  constructor(config) {
    this.config = config
  }

  sorted(timeExtractor, data) {
    const sortedData = [...data]
    sortedData.sort((a, b) =>
      timeExtractor.difference(timeExtractor.getObservationTime(a), timeExtractor.getObservationTime(b)),
    )
    return sortedData
  }

  joinData = memoize((timeExtractor, ...observations) => {
    const joinedSeries = []
    for (let oi = 0; oi < observations.length - 1; oi += 2) {
      let xObs = observations[oi]
      let yObs = observations[oi + 1]
      xObs.data = filterOnlineOfflineData(xObs.data, this.config)
      yObs.data = filterOnlineOfflineData(yObs.data, this.config)
      if (this.config.outlierFilter) {
        const [xmin, xmax] = getRangeWithoutOutliers(xObs, (obs) => obs.value)
        if (isNumber(xmin) && isNumber(xmax)) {
          xObs.data = xObs.data.filter((obs) => obs.value >= xmin && obs.value <= xmax)
        }
        const [ymin, ymax] = getRangeWithoutOutliers(yObs, (obs) => obs.value)
        if (isNumber(ymin) && isNumber(ymax)) {
          yObs.data = yObs.data.filter((obs) => obs.value >= ymin && obs.value <= ymax)
        }
      }

      joinedSeries.push(this.joinDataSeries(timeExtractor, xObs, yObs))
    }
    return joinedSeries
  })

  joinDataSeries(timeExtractor, xObs, yObs) {
    if (!xObs || !xObs.data) return
    if (!yObs || !yObs.data) return

    const xData = this.sorted(timeExtractor, xObs.data),
      yData = this.sorted(timeExtractor, yObs.data)

    let xi = 0,
      yi = 0

    const joined = []
    let lastX = null,
      lastY = null

    while (xi < xData.length && yi < yData.length) {
      const xo = xData[xi],
        yo = yData[yi]

      const timeDiff = timeExtractor.difference(
        timeExtractor.getObservationTime(xo),
        timeExtractor.getObservationTime(yo),
      )

      if (timeDiff >= 0) {
        lastY = yo
        ++yi

        if (lastX !== null) {
          /* check time diff as well? */ joined.push({
            x: lastX.value,
            y: lastY.value,
            timestamp: lastY.timestamp,
            day: lastY.day,
          })
        }
      } else {
        lastX = xo
        ++xi

        if (lastY !== null) {
          /* check time diff as well? */ joined.push({
            x: lastX.value,
            y: lastY.value,
            timestamp: lastX.timestamp,
            day: lastX.day,
          })
        }
      }
    }

    return joined
  }

  getPlotProps(run, viewerState) {
    const { observationAccessor, observationTypes } = run
    const { timeExtractor } = viewerState

    if (!observationAccessor) return {}

    const obsData = (this.config.observations || []).map((obv) => {
      const accessed_data = observationAccessor(obv.tag, obv.units, obv.smp_src)
      if (accessed_data) {
        return {
          ...obv,
          ...accessed_data,
        }
      } else {
        return { missing: true }
      }
    })

    const joinedData = this.joinData(timeExtractor, ...obsData)

    return {
      tags: this.config.observations || [],
      joinedData: joinedData,
      xRange: joinedData && this.getXRange(joinedData),
      yRange: joinedData && this.getYRange(joinedData),
      timeRange: joinedData && this.getTimeRange(joinedData, timeExtractor),
      loading: obsData.some((obs) => obs.loading),
      errorLayers: obsData.filter((obs) => obs.err),

      // These aren't needed for the individual plots, but must be captured for the plot builder interface
      observationTypes: observationTypes,
      queryTags: viewerState.queryTags || [],
    }
  }

  getXRange = (joinedData) => this.joinedDataExtent(joinedData, (d) => d.x)
  getYRange = (joinedData) => this.joinedDataExtent(joinedData, (d) => d.y)
  getTimeRange = memoize((joinedData, timeExtractor) =>
    this.joinedDataExtent(joinedData, timeExtractor.getObservationTime),
  )

  joinedDataExtent(series, accessor) {
    const extents = []

    for (const s of series) {
      if (s) {
        extents.push(...extent(s, accessor))
      }
    }

    if (extents.length > 0) {
      let [minVal, maxVal] = extent(extents)
      if (minVal === maxVal) {
        minVal = minVal - 0.005
        maxVal = maxVal + 0.005
      }
      return [minVal, maxVal]
    }
  }

  getTimeAxes(run, viewerState, timeExtractor) {
    return this.getAxesAndRanges(run, viewerState, timeExtractor)
  }

  getAxesAndRanges(run, viewerState, timeExtractor) {
    const { xRange, yRange } = this.getPlotProps(run, viewerState)
    return {
      x: xRange,
      y: yRange,
    }
  }
}
